import React from 'react';
import { analyticsService } from '@common/analytics';
import { trackMPEventAdditionalFundsRequired } from '@app/actions/payment_options';
import {
  trackAddCreditCardFailed,
  trackCreditCardAdded,
  trackPaymentMethodChanged,
  trackPaymentMethodClickedEvent,
  trackStartAddingCreditCard,
  trackPaymentSourcesEvent
} from '@app/actions/payment_sources';
import withPGTranslation from '../../../config/withPGTranslation';
import { purchaseDetailsTypes, paymentSourcesTheme } from '../../../../common/config/const';
import PurchaseDetailsCommon from '../../../../common/purchase_details_common/purchase_details_common';
import { log, LOG_LEVELS } from '../../../../common/config/app_logger';
import PayPalProcessing from '../../purchases/pay_pal_processing';
import CreditCardProcessing from '../../purchases/credit_card_processing';
import { getCurrentLocalization } from '../../../config/i18n';
import {
  getAddFundsInitialChargedAndInitialDurationPropsNames,
  matchBannerRoutes, getXfmFreeSetupMin, getXfmDefaultMinutes, isXFMActive, isPrerenderMode
} from '../../../config/util';
import CreditCardValidateProcessing from '../../purchases/credit_card_validate_processing';
import { useCurrentPath } from '../../routes/hooks/useCurrentPath';

function PurchaseDetails({
  purchaseDetails = {},
  clientAvailableCredit = '0',
  hidePaymentDetails,
  loadAvailable,
  purchaseSuccess,
  options = [],
  loadingOptions,
  sideMenuPurcahseSuccess,
  isContinue = false,
  chatType,
  affiliatePromoPurcahseSuccess,
  doneChangingPaymentSource,
  doneAddCard,
  paymentSources = {},
  addSource,
  startEditingPaymentSource,
  managePaymentSource,
  onMakeDefault,
  loadingSources,
  doneManagingPaymentSource,
  editPaymentSource,
  updatePaymentSource,
  clearAddingCardError,
  loadPaymentOptions,
  changingPaymentSource,
  reset,
  hideAffiliatePromoPopup,
  noThanksLinkClickedAction,
  backArrowAction,
  editCardIfSecurityReasons,
  isPayg,
  creditCardValidateError,
  loadPaymentOptionsOnClose,
  affiliatePromoPopupOnCloseIconClick,
  advisorId,
  cancelChatOnPP,
  userBonusInfos,
  anonymousUserBonusInfos,
  userXfmProgram,
  userId
}) {
  const { routeKey } = useCurrentPath();
  const {
    bonus, clickSource, purchaseType, flow
  } = purchaseDetails;

  const trackAddFundsScreenEvent = (funds) => {
    const {
      initialChargedPropName, initialDurationPropName
    } = getAddFundsInitialChargedAndInitialDurationPropsNames({ chatType });
    const additionalFundsParams = {
      options,
      clickSource,
      credit: funds,
      bonus: bonus || 0,
      initialChargedPropName,
      initialDurationPropName,
      payAsYouGo: isPayg,
      flow
    };
    trackMPEventAdditionalFundsRequired(additionalFundsParams);
  };

  const onPurchaseDetailsScreenOpened = () => {
    loadPaymentOptions({ ignoreSkip: true });
    // loadAvailable(isPayg);
    loadAvailable(true);
    analyticsService.trackPageVisit('purchase credit? draw');
    return () => {
      reset();
    };
  };

  const onPurchaseComplete = () => {
    if (purchaseType === purchaseDetailsTypes.SIDE_MENU) {
      sideMenuPurcahseSuccess();
      return;
    }
    if (purchaseType === purchaseDetailsTypes.AFFILIATE_PROMO_POPUP) {
      affiliatePromoPurcahseSuccess();
      return;
    }
    purchaseSuccess();
  };

  const paymentProcessing = (currentPaymentOption, purchaseURL, purchaseAmount, callback) => (
    <PayPalProcessing
      purchaseURL={ purchaseURL }
      onPurchaseComplete={ onPurchaseComplete }
      type={ purchaseType }
      purchaseAmount={ purchaseAmount }
      currentPaymentOption={ currentPaymentOption }
      hasPaypalBillingAgreement={ (currentPaymentOption.context || {}).hasPaypalBillingAgreement }
      bonus={ bonus }
      isContinue={ isContinue }
      chatType={ chatType }
    >
      {callback()}
    </PayPalProcessing>
  );

  const renderCreditCardProcessing = (disableBuyButton, isMinPurchase, currentPaymentOption, purchaseURL, purchaseAmount, callback) => (
    <CreditCardProcessing
      purchaseURL={ purchaseURL }
      onPurchaseComplete={ onPurchaseComplete }
      type={ purchaseType }
      purchaseAmount={ purchaseAmount }
      isMinPurchase={ isMinPurchase }
      disabled={ !!disableBuyButton }
      currentPaymentOption={ currentPaymentOption }
      bonus={ bonus }
      isContinue={ isContinue }
      chatType={ chatType }
    >
      {callback()}
    </CreditCardProcessing>
  );

  const renderPayPalProcessing = (disableBuyButton, isMinPurchase, currentPaymentOption, purchaseURL, purchaseAmount, callback) => (
    <PayPalProcessing
      purchaseURL={ purchaseURL }
      onPurchaseComplete={ onPurchaseComplete }
      type={ purchaseType }
      purchaseAmount={ purchaseAmount }
      isMinPurchase={ isMinPurchase }
      disabled={ !!disableBuyButton }
      currentPaymentOption={ currentPaymentOption }
      bonus={ bonus }
      isContinue={ isContinue }
      chatType={ chatType }
    >
      {callback()}
    </PayPalProcessing>
  );

  const renderCreditCardValidateProcessing = ({
    disableBuyButton, paymentMethodListAnalyticsEvents
  }) => (
    <CreditCardValidateProcessing
      onCreditCardValidated={ onPurchaseComplete }
      disableBuyButton={ disableBuyButton }
      loading={ loadingSources }
      requiresPayAsYouGoValidation={ options && options[0]?.requiresPayAsYouGoValidation }
      advisorId={ advisorId }
      chatType={ chatType }
      currentPaymentSource={ options && options[0] }
      paymentMethodListAnalyticsEvents={ paymentMethodListAnalyticsEvents }
    />
  );

  const hideAffiliatePromoPopupAction = () => {
    hideAffiliatePromoPopup();
    noThanksLinkClickedAction();
  };

  const closeAffiliatePromoPopupAction = () => {
    if (affiliatePromoPopupOnCloseIconClick) {
      affiliatePromoPopupOnCloseIconClick();
    }
  };

  const showPaymentSources = (kind) => {
    log(LOG_LEVELS.INFO, 'CC', '<Option/> changing PaymentSource');
    changingPaymentSource(kind || true);
  };

  const addCreditCard = () => {
    log(LOG_LEVELS.INFO, 'CC', 'on add Credit card');
    addSource('cc');
    showPaymentSources('cc');
  };

  const paymentMethodListActions = {
    loadAvailable,
    addSource,
    startEditingPaymentSource,
    managePaymentSource,
    doneManagingPaymentSource,
    getAddCardCinfig: editPaymentSource,
    updatePaymentSource,
    doneAddCard,
    clearAddingCardError,
    doneChangingPaymentSource,
    showPaymentSources,
    loadPaymentOptions,
    editCardIfSecurityReasons
  };

  const paymentMethodListAnalyticsEvents = {
    trackPaymentSourcesEvent,
    trackPaymentMethodChanged,
    trackStartAddingCreditCard,
    trackCreditCardAdded,
    trackAddCreditCardFailed
  };

  const purchaseDetailsActions = {
    hidePaymentDetails: hidePaymentDetails ?? closeAffiliatePromoPopupAction,
    backArrowAction,
    onPurchaseDetailsScreenOpened,
    renderCreditCardProcessing,
    renderPayPalProcessing,
    paymentProcessing,
    getCurrentLocalization,
    addCreditCard,
    renderCreditCardValidateProcessing,
    hideAffiliatePromoPopupAction,
    onPurchaseComplete,
    cancelChatOnPP
  };

  const topBannerProps = {
    userBonusInfos,
    anonymousUserBonusInfos,
    userXfmProgram,
    userId,
    shouldShowMarketingBanner: matchBannerRoutes(routeKey),
    xfmFreeSetupMin: getXfmFreeSetupMin(),
    xfmDefaultMinutes: getXfmDefaultMinutes(),
    isXFMActive: isXFMActive(),
    isPrerenderMode: isPrerenderMode()
  };

  const renderPurchaseDetailsCommon = () => {
    const purchaseDetailsCommonProps = {
      ...purchaseDetailsActions,
      paymentMethodListActions,
      paymentMethodListAnalyticsEvents,
      trackAddFundsScreenEvent,
      showPaymentSources,
      onMakeDefault,
      log,
      clientAvailableCredit,
      options,
      loadingOptions,
      loadingSources,
      paymentSources,
      theme: paymentSourcesTheme.purchase,
      purchaseDetails,
      trackPageVisit: (pageTitle) => analyticsService.trackPageVisit(pageTitle),
      isPayg,
      creditCardValidateError,
      loadPaymentOptionsOnClose,
      chatType,
      isContinue,
      topBannerProps,
      trackPaymentMethodClickedEvent
    };
    return <PurchaseDetailsCommon { ...purchaseDetailsCommonProps } />;
  };

  return renderPurchaseDetailsCommon();
}

export default withPGTranslation(PurchaseDetails);
